import { useState } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Loader2, PlayCircle, RotateCcw, Copy, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

const errText = (e) => { const d = e.response?.data?.detail; return typeof d === 'string' ? d : d?.message || 'No se pudo cargar la demostración'; };

function CredentialsDialog({ result, onClose }) {
    const copyAll = () => {
        const txt = [`Acceso: ${result.login_url}`, ...result.credentials.map(c => `${c.role_label}: ${c.email} / ${c.password}`)].join('\n');
        navigator.clipboard?.writeText(txt).then(() => toast.success('Credenciales copiadas')).catch(() => toast.error('No se pudo copiar'));
    };
    return (
        <Dialog open={!!result} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-[680px]" data-testid="demo-credentials-dialog">
                <DialogHeader>
                    <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase tracking-tight">Demostración lista</DialogTitle>
                    <DialogDescription>
                        {result.summary?.flights} vuelos, {result.summary?.crew} tripulantes y {result.summary?.aircraft} aeronaves cargados para el {result.date}. Vuelo del recorrido: <b>{result.live_flight}</b>.
                    </DialogDescription>
                </DialogHeader>
                <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-400" data-testid="demo-credentials-warning">
                    <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    Estas contraseñas se muestran una sola vez y no se guardan. Cópialas ahora; si las pierdes, reinicia la demostración para generar otras.
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="demo-credentials-table">
                        <thead><tr className="text-left text-muted-foreground"><th className="py-1.5">Rol</th><th>Correo</th><th>Contraseña</th></tr></thead>
                        <tbody>
                            {result.credentials.map(c => (
                                <tr key={c.email} className="border-t border-border/40" data-testid={`demo-cred-${c.role}`}>
                                    <td className="py-1.5">{c.role_label}</td>
                                    <td className="font-mono text-xs">{c.email}</td>
                                    <td className="font-mono text-xs select-all">{c.password}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p className="text-xs text-muted-foreground">Acceso: <span className="font-mono">{result.login_url}</span></p>
                <DialogFooter>
                    <Button variant="outline" onClick={copyAll} data-testid="demo-copy-credentials-btn"><Copy className="w-4 h-4 mr-2" />Copiar todo</Button>
                    <Button onClick={onClose} data-testid="demo-credentials-close-btn">Ya las guardé</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export function useDemoRunner(apiCall, onDone) {
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);
    const run = async (url, confirmMsg) => {
        if (confirmMsg && !window.confirm(confirmMsg)) return;
        setRunning(true);
        try {
            const r = await apiCall('post', url);
            setResult(r.data);
            onDone?.(r.data);
        } catch (e) {
            toast.error(errText(e), { duration: 10000 });
        } finally { setRunning(false); }
    };
    const dialog = result ? <CredentialsDialog result={result} onClose={() => setResult(null)} /> : null;
    return { running, run, dialog };
}

export function CreateDemoButton({ apiCall, onDone, exists }) {
    const { running, run, dialog } = useDemoRunner(apiCall, onDone);
    return (
        <>
            {!exists && <Button variant="outline" disabled={running} onClick={() => run('/admin/demo/provision', 'Se creará la «Aerolínea Demo» con datos ficticios. ¿Continuar?')} data-testid="create-demo-tenant-btn">
                {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />} Crear aerolínea demo
            </Button>}
            {dialog}
        </>
    );
}

export function DemoResetCard({ tenant, apiCall, onDone }) {
    const { running, run, dialog } = useDemoRunner(apiCall, onDone);
    if (!tenant?.is_demo) return null;
    return (
        <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 p-4 space-y-2" data-testid="demo-reset-card">
            <p className="text-sm font-semibold">Empresa de demostración</p>
            <p className="text-xs text-muted-foreground">Borra solo los datos de esta empresa y vuelve a cargar la flota, tripulación y vuelos de ayer, hoy y mañana. Genera contraseñas nuevas para los 8 usuarios.</p>
            <Button size="sm" disabled={running} onClick={() => run(`/admin/tenants/${tenant.id}/demo/reset`, '¿Reiniciar la demostración? Se borrarán todos los cambios hechos en «Aerolínea Demo».')} data-testid="demo-reset-btn">
                {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />} Cargar / reiniciar demostración
            </Button>
            {dialog}
        </div>
    );
}
